import { CanActivateChildFn, CanActivateFn } from '@angular/router';
import { UserRole } from '../../auth/models';
import { createSequentialGuard } from './create-sequential-guard';
import { hasAcceptedDPA } from './has-accepted-dpa';
import { hasAcceptedTermsOfUse } from './has-accepted-terms-of-use';
import { hasOrganization } from './has-organization';
import { hasOrganizationApproval } from './has-organization-approval';
import { isAuthenticated } from './is-authenticated';
import { isAuthorized, IsAuthorizedGuardOptions } from './is-authorized';


export interface InternalMasterGuardOptions {
  roles: UserRole | UserRole[] | Partial<IsAuthorizedGuardOptions>;
}

/**
 * Create a master guard that runs multiple checks in sequence to validate the current user before allowing access to a route. Checks that:
 *
 *   1. The user is authenticated
 *   2. The user is authorized to access the route (if roles are provided in the options)
 *   3. The user has accepted the terms of use
 *   4. The user is assigned to an organization
 *   5. The user has accepted the data processing agreement
 *   6. The user (if service) has organization approval
 *
 * @returns {CanActivateFn | CanActivateChildFn} A guard that runs all inner guards in sequence.
 * @param options The options for the internal master guard.
 *
 * @example ```typescript
 * {
 *    // Route that can only be accessed by authenticated admin users that have an organization and accepted the DPA and terms of use
 *    path: 'protected',
 *    component: ProtectedComponent,
 *    canActivate: [internalMasterGuard({ roles: [MmuUserRoles.ADMIN] })]
 * }
 * ```
 */
export function internalMasterGuard(options: Partial<InternalMasterGuardOptions> = {}): CanActivateFn | CanActivateChildFn {
  return createSequentialGuard([
    isAuthenticated(),
    ...(options.roles ? [isAuthorized(options.roles)] : []),
    hasAcceptedTermsOfUse(),
    hasOrganization(),
    hasAcceptedDPA(),
    hasOrganizationApproval()
  ]);
}

